import { useState } from "react";
import { Menu, X } from "lucide-react";
import Icon from "@/components/Icon";

// AppShell — pure layout. Sidebar + topbar + content. Mobile-first (drawer).
export function AppShell({ brand = "Restaurangkollen", navItems = [], activeKey, onNavigate,
                           user, topbarRight, children, testid = "app-shell" }) {
  const [open, setOpen] = useState(false);
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const Sidebar = (
    <aside className="flex h-full w-[260px] flex-col bg-card border-r border-border">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-border">
        <img src="/logo.png" alt="Restaurangkollen" className="h-8 w-8 rounded-full shrink-0 object-cover" />
        <span className="font-display text-sm font-bold tracking-tight flex-1 truncate">{brand}</span>
        <button className="lg:hidden text-muted-foreground" onClick={() => setOpen(false)} data-testid="shell-close"><X className="h-5 w-5" /></button>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navItems.map((it) => {
          const active = it.key === activeKey;
          return (
            <button key={it.key} onClick={() => { onNavigate?.(it.key); setOpen(false); }}
              data-testid={`shell-nav-${it.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all duration-200 ${
                active ? "bg-secondary text-foreground font-semibold" : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
              }`}>
              <Icon name={it.icon} className="h-[18px] w-[18px]" />
              <span className="flex-1 text-left truncate">{it.label}</span>
              {it.badge ? <span className="text-[0.62rem] font-mono px-1.5 py-0.5 rounded-md bg-danger/15 text-[hsl(0_80%_72%)]">{it.badge}</span> : null}
            </button>
          );
        })}
      </nav>
      {user && (
        <div className="border-t border-border p-3 flex items-center gap-3">
          {user.avatar
            ? <img src={user.avatar} alt={user.name} className="h-9 w-9 rounded-full object-cover shrink-0" />
            : <span className="h-9 w-9 grid place-items-center rounded-full bg-accent text-white text-xs font-bold shrink-0">{initials}</span>}
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{user.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user.title || user.role}</div>
          </div>
        </div>
      )}
    </aside>
  );

  return (
    <div data-testid={testid} className="flex h-full min-h-screen bg-background">
      <div className="hidden lg:block">{Sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center gap-3 h-14 px-4 border-b border-border bg-card/60 backdrop-blur sticky top-0 z-30">
          <button className="lg:hidden text-muted-foreground" onClick={() => setOpen(true)} data-testid="shell-open"><Menu className="h-5 w-5" /></button>
          <div className="flex-1" />
          {topbarRight}
        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}

export function PageHeader({ overline, title, description, actions, icon, testid = "page-header" }) {
  return (
    <div data-testid={testid} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div className="flex items-start gap-4 min-w-0">
        {icon}
        <div className="min-w-0">
          {overline && <p className="overline text-muted-foreground mb-2">{overline}</p>}
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-none">{title}</h1>
          {description && <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed text-sm">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

// ModuleHeader — department header + functional module tabs (regel 7).
export function ModuleHeader({ department = {}, tabs = [], active, onTab, actions, testid = "module-header" }) {
  return (
    <div data-testid={testid} className="border-b border-border">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 grid place-items-center rounded-xl text-white shrink-0" style={{ background: department.color || "#4F6BED" }}>
          <Icon name={department.icon} className="h-6 w-6" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="overline text-muted-foreground mb-1">Avdelning</p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-none">{department.name}</h1>
          {department.description && <p className="text-muted-foreground mt-2 text-sm max-w-2xl">{department.description}</p>}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
      <div className="flex gap-1 mt-6 overflow-x-auto no-scrollbar -mb-px">
        {tabs.map((t) => {
          const isActive = t.key === active;
          return (
            <button key={t.key} onClick={() => onTab?.(t.key)} data-testid={`module-tab-${t.key}`}
              className={`flex items-center gap-2 px-3 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
                isActive ? "border-accent text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}>
              {t.icon && <Icon name={t.icon} className="h-4 w-4" />}
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
